import { Form, Input, Flex, Typography, Spin } from "antd";
import { useEffect, useState } from "react";
import type { FormInstance } from "antd";
import { Customer } from "@lib/types/Customer";
import { customerSchema as yupSchema } from "@lib/schemas/customer";
import { createYupSync } from "@lib/helpers/TableHelper";
import { healthCheckCrmDomain } from "@lib/helpers/UrlHelper";
import { MdWarning } from "react-icons/md";
import { FaCheck, FaPencilAlt } from "react-icons/fa";
import useIntl from "react-intl/lib/src/components/useIntl";

const HEALTHCHECK_SUFFIX = {
  crm: "/api/v1/healthcheck",
  whatsapp: "/api/v1/admin/health-check",
};

type Props = {
  form: FormInstance<Customer>;
  customer: Customer;
  initialValues: Customer;
};

const APISettings: React.FC<Props> = ({
  form,
  customer: _customer,
  initialValues: _initialValues,
}) => {
  const { messages } = useIntl();
  const [domainChecking, setDomainChecking] = useState<boolean>(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const domain = Form.useWatch("domain", form);
  const isWhatsappPlatform = Form.useWatch("isWhatsappPlatform", form);

  useEffect(() => {
    if (!domain || domain.trim().length === 0) {
      setDomainChecking(false);
      setDomainError(null);
      return;
    }

    setDomainChecking(true);
    healthCheckCrmDomain(
      domain,
      isWhatsappPlatform ? HEALTHCHECK_SUFFIX.whatsapp : HEALTHCHECK_SUFFIX.crm,
    )
      .then((result) => {
        if (result.status) {
          setDomainError(null);
        } else {
          setDomainError(result.message);
        }
      })
      .finally(() => {
        setDomainChecking(false);
      });
  }, [domain, isWhatsappPlatform]);

  return (
    <div style={{ padding: "24px" }}>
      <Form.Item
        name="domain"
        label={(messages["common.crmApiUrl"] as string) || "CRM API URL"}
        rules={[createYupSync({ schema: yupSchema })]}
        extra={
          (messages["common.crmApiUrl.info"] as string) ||
          "Enter the CRM API domain URL"
        }
      >
        <Input
          value={domain}
          addonAfter={
            <Flex align="center" gap={8}>
              <Typography.Text type="secondary">
                {isWhatsappPlatform
                  ? HEALTHCHECK_SUFFIX.whatsapp
                  : HEALTHCHECK_SUFFIX.crm}
              </Typography.Text>

              {form.getFieldValue("domain")?.length == 0 || domainChecking ? (
                <>
                  {domainChecking ? <Spin /> : <FaPencilAlt color="orange" />}
                </>
              ) : (
                <>
                  {!!domainError ? (
                    <MdWarning color="red" />
                  ) : (
                    <FaCheck color="green" />
                  )}
                </>
              )}
            </Flex>
          }
        />
      </Form.Item>

      {/* Future API settings fields will be added here */}
    </div>
  );
};

export default APISettings;
